import React, { useEffect, useRef, useState } from 'react';
import { StyledSecondHeader } from './style';
import { useIntl } from 'react-intl';
import { Button, Icon, Search, Tag } from 'ui';
import { useCategoryState } from '../../../context';
import { CategoryData } from 'types/CategoryData';
import CategoryMenu from '../../../../ui/Menu/CategoryMenu';
import { useNavigate } from 'react-router-dom';

import { Dropdown, MenuProps } from 'antd';
import ProfileMenu from '../ProfileMenu';
import CartDrawer from 'ui/Drawer/Drawers/CartDrawer';
import CartAddedPopup from '../CartAddedPopup';
import { useCartState } from 'public/context/CartContext';
import { useAuth } from '../../../../hooks/useAuth';
import useMarketType from '../../../../utils/useMarketType';
import PublicRoute, { publicPath } from 'enums/publicRoutes';

interface DefaultSecondHeaderProps {
  isModernHeader?: boolean;
}

const DefaultSecondHeader = ({
  isModernHeader = true,
}: DefaultSecondHeaderProps) => {
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState<boolean>(false);
  const [menuData, setMenuData] = useState<CategoryData[]>([]);
  const [showDropdownOverlay, setShowDropdownOverlay] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const intl = useIntl();
  const navigate = useNavigate();
  const { isEmarket } = useMarketType();

  const { priceTotal } = useCartState();
  const { category } = useCategoryState();
  const { user, verifySession } = useAuth();
  const closeMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <ProfileMenu fullName={`${user?.name ?? ''} ${user?.surname ?? ''}`} />
      ),
    },
  ];

  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (category ? category : []) {
      const categories: CategoryData[] =
        Object.values(category ? category : []) || [];
      setMenuData(categories);
    }
  }, [category]);

  const clearCloseMenuTimeout = () => {
    if (closeMenuTimeoutRef.current) {
      clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = null;
    }
  };

  const handleMenuMouseEnter = () => {
    clearCloseMenuTimeout();
    setShowMenu(true);
  };

  const handleMenuMouseLeave = () => {
    clearCloseMenuTimeout();
    closeMenuTimeoutRef.current = setTimeout(() => {
      setShowMenu(false);
    }, 120);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  };

  const handleNavigateProducts = () => {
    clearCloseMenuTimeout();
    navigate(
      isEmarket ? PublicRoute.PRODUCTS_ALL : PublicRoute.WHOLESALE_PRODUCTS_ALL
    );
    setShowMenu(false);
  };

  const handleOnClickFarms = () => {
    navigate(isEmarket ? PublicRoute.MAP : PublicRoute.WHOLESALE_MAP, {
      state: {
        dataType: 'farms',
      },
    });
    setShowMenu(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      clearCloseMenuTimeout();
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const onCartClose = () => {
    setIsCartDrawerOpen(false);
  };

  return (
    <StyledSecondHeader
      className={
        isModernHeader
          ? `home-header ${!isEmarket ? 'wholesale-header' : ''}`
          : ''
      }
    >
      <div className="content-wrapper">
        <div className="container">
          <div className="content">
            <div className="block block-1">
              {isModernHeader && (
                <span
                  className="home-logo"
                  onClick={() => navigate(PublicRoute.HOME)}
                >
                  <Icon name={'NG-peleks-2.png'} type={'header'} svg={false} />
                </span>
              )}
              <Button
                onMouseEnter={handleMenuMouseEnter}
                onMouseLeave={handleMenuMouseLeave}
                icon={<Icon name={'arrow-down-white'} type={'small'} />}
                iconPosition={'end'}
                size={'large'}
                className="products-link"
                label={intl.formatMessage({ id: 'general.products' })}
                onClick={handleNavigateProducts}
              />
              {showMenu && !!menuData.length && (
                <div
                  ref={menuRef}
                  className={'menu-pos'}
                  onMouseEnter={handleMenuMouseEnter}
                  onMouseLeave={handleMenuMouseLeave}
                >
                  <CategoryMenu
                    categories={menuData}
                    onCloseMenu={() => setShowMenu(false)}
                  />
                </div>
              )}
              <Button
                type="primary"
                size={'small'}
                label={intl.formatMessage({ id: 'general.manufacturers' })}
                onClick={handleOnClickFarms}
                className="manufacturers-link"
              />
              {!isEmarket && (
                <Button
                  size="small"
                  label={intl.formatMessage({ id: 'general.listings' })}
                  href={PublicRoute.WHOLESALE_LISTINGS}
                  className="listings-link"
                />
              )}
              {isEmarket && (
                <Button
                  type="text"
                  label={intl.formatMessage({ id: 'general.gift_sets' })}
                  onClick={() => {
                    navigate(publicPath.giftSets());
                  }}
                  className="gift-sets-link"
                />
              )}
            </div>
            <div className="header-search">
              <Search
                prefix={<Icon name={'search-icon-black'} />}
                size="large"
                placeholder={intl.formatMessage({
                  id: 'general.search_by_taste',
                })}
              />
            </div>
            <div className="second-header-btn-wrapper">
              <div className="block block-2">
                <Button
                  type="text"
                  label={
                    <span className="sales-label">
                      <Tag className="sales-tag">%</Tag>
                      {intl.formatMessage({ id: 'general.sales' })}
                    </span>
                  }
                  href={
                    isEmarket
                      ? PublicRoute.PRODUCTS_ALL_DISCOUNTED
                      : PublicRoute.WHOLESALE_PRODUCTS_ALL_DISCOUNTED
                  }
                  className="sales-link"
                />

                <div className={'second-header-btn-wrapper'}>
                  <div className={'second-header-cart-total'}>
                    {priceTotal.toFixed(2) + ' €'}
                  </div>
                  <Button
                    type="text"
                    label={intl.formatMessage({ id: 'general.my_basked' })}
                    icon={<Icon name={'shopping-cart-black'} />}
                    onClick={() => setIsCartDrawerOpen(true)}
                  />
                </div>
              </div>
              {user?.authenticated ? (
                <Dropdown
                  menu={{ items }}
                  placement="bottomRight"
                  onOpenChange={(open) => {
                    setShowDropdownOverlay(open);
                    if (open) {
                      verifySession();
                    }
                  }}
                  getPopupContainer={(trigger) =>
                    trigger.parentNode as HTMLElement
                  }
                  arrow={true}
                >
                  <div className={'profile-button'}>
                    <Button
                      type="text"
                      label={intl.formatMessage({ id: 'general.profile' })}
                      icon={<Icon name={'user-profile-black'} />}
                    />
                    <Icon name={'arrow-down'} className={'profile-icon'} />
                  </div>
                </Dropdown>
              ) : (
                <Button
                  className="profile-btn"
                  type="text"
                  label={intl.formatMessage({ id: 'general.profile' })}
                  icon={<Icon name={'user-profile-black'} />}
                  onClick={() => navigate(PublicRoute.LOGIN)}
                />
              )}
              {isEmarket ? (
                <Button
                  label={intl.formatMessage({ id: 'general.wholesale_header' })}
                  onClick={() => navigate(PublicRoute.WHOLESALE_PRODUCTS_ALL)}
                  className="bizness-link"
                />
              ) : (
                <Button
                  label={intl.formatMessage({ id: 'general.e-market' })}
                  onClick={() => navigate(PublicRoute.HOME)}
                  className="back-emarket-link"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <CartAddedPopup
        suppressed={isCartDrawerOpen}
        onOpenCart={() => setIsCartDrawerOpen(true)}
      />
      <CartDrawer
        onCartClose={onCartClose}
        isCartDrawerOpen={isCartDrawerOpen}
      />
      {showMenu && (
        <div className="overlay" onClick={() => setShowMenu(false)} />
      )}
      {showDropdownOverlay && (
        <div
          className="overlay"
          onClick={() => setShowDropdownOverlay(false)}
        />
      )}
    </StyledSecondHeader>
  );
};

export default DefaultSecondHeader;
